import React, { useState, useEffect, useCallback} from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import SEOHead from '../../components/SEOHead';
import { generateListingTitle, generateListingDescription } from '../../utils/seo';
import { pickListingSchema, breadcrumbSchema } from '../../utils/structuredData';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Skeleton } from '../../components/ui/skeleton';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../../components/ui/carousel';
import { useSiteLogo } from '../../contexts/SiteSettingsContext';
import { 
  MapPin, Phone, Mail, Eye, Calendar, ChevronLeft, Share2, Heart,
  Star, Clock, User, Tag, CheckCircle, Map, Edit2, MessageCircle, Loader2, Bell, BellPlus, Shield, Award, Zap, Crown,
  Briefcase, GraduationCap, Send, Upload, FileText
} from 'lucide-react';
import FavoriteButton from '../../components/FavoriteButton';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { getFieldLabel } from '../../utils/fieldLabels';
import { EnrichedSchemaCard } from '../../components/EnrichedSchemaCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { formatPriceInput, parsePriceInput, formatPrice } from '../../utils/format';
import L from 'leaflet';
import { useSiteName } from '../../contexts/SiteSettingsContext';
import { MessageDialog, PriceAlertDialog, ImageLightbox, JobApplicationDialog } from './ListingDialogs';

// Leaflet default icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

import { ATTRIBUTE_LABELS, formatAttributeValue, shouldShowAttribute } from './constants';

const ListingDetailPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { logoUrl: siteLogoUrl } = useSiteLogo();
  const watermarkSrc = () => siteLogoUrl || '/logo.png';
  const watermarkFallback = '/logo.png';
  const [listing, setListing] = useState(null);
  const [owner, setOwner] = useState(null);
  const [category, setCategory] = useState(null);
  const [listingSchemaData, setListingSchemaData] = useState(null);
  const [relatedListings, setRelatedListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  
  // Job application state
  const [jobAppDialogOpen, setJobAppDialogOpen] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [submittingApp, setSubmittingApp] = useState(false);
  const [jobAppForm, setJobAppForm] = useState({
    full_name: '', phone: '', email: '', cover_letter: '',
    experience_summary: '', current_job: '', education: '',
    skills: '', salary_expectation: '', availability_date: '',
    cv_url: '', cv_file_name: '',
  });
  const [uploadingCV, setUploadingCV] = useState(false);
  
  // Message dialog state
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Price alert state
  const [priceAlertDialogOpen, setPriceAlertDialogOpen] = useState(false);
  const [targetPrice, setTargetPrice] = useState('');
  const [targetPriceDisplay, setTargetPriceDisplay] = useState('');
  const [alertType, setAlertType] = useState('below');
  const [savingAlert, setSavingAlert] = useState(false);
  const [hasPriceAlert, setHasPriceAlert] = useState(false);
  
  // Owner verification badges
  const [ownerVerification, setOwnerVerification] = useState(null);
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);


  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      // User giriş yapmamış
    }
  };

  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const response = await axios.get(`${API}/listings/${listingId}/reviews?limit=20`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [listingId]);

  const checkJobApplication = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/job-applications/check/${listingId}`);
      setHasApplied(res.data.has_applied);
    } catch (err) { /* silently handled */ }
  }, [listingId]);

  const handleJobAppSubmit = async () => {
    if (!jobAppForm.full_name || !jobAppForm.phone || !jobAppForm.cover_letter) {
      toast.error('Full name, phone and cover letter are required');
      return;
    }
    setSubmittingApp(true);
    try {
      await axios.post(`${API}/job-applications`, {
        listing_id: listingId,
        ...jobAppForm,
        skills: jobAppForm.skills ? jobAppForm.skills.split(',').map(s => s.trim()) : [],
      });
      toast.success('Your application has been submitted! You can track it on the My Requests page.');
      setJobAppDialogOpen(false);
      setHasApplied(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit application');
    } finally {
      setSubmittingApp(false);
    }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('CV file must be less than 10MB'); return; }
    setUploadingCV(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/job-applications/upload-cv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setJobAppForm(prev => ({ ...prev, cv_url: res.data.url, cv_file_name: res.data.file_name }));
      toast.success('CV uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CV could not be loaded');
    } finally {
      setUploadingCV(false);
    }
  };

  const openJobAppDialog = () => {
    if (!currentUser) {
      toast.error('Please sign in to apply');
      return;
    }
    setJobAppForm(prev => ({
      ...prev,
      full_name: currentUser.name || '',
      email: currentUser.email || '',
      phone: currentUser.phone || '',
    }));
    setJobAppDialogOpen(true);
  };


  const fetchListing = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/listings/${listingId}`);
      setListing(response.data);

      // Son görüntülenenlere ekle (arka planda, sessizce)
      try {
        await axios.post(`${API}/users/me/recently-viewed/${listingId}`);
      } catch (e) {
        // Sessizce geç (kullanıcı giriş yapmamış olabilir)
      }

      // Fetch owner info
      if (response.data.user_id) {
        try {
          const ownerRes = await axios.get(`${API}/users/${response.data.user_id}/public`);
          setOwner(ownerRes.data);
          
          // Fetch owner verification badges
          const verificationRes = await axios.get(`${API}/verification/user/${response.data.user_id}`);
          setOwnerVerification(verificationRes.data);
        } catch (e) {
          // Owner info not available
        }
      }

      // Fetch category
      if (response.data.category_id) {
        const catResponse = await axios.get(`${API}/categories/${response.data.category_id}`);
        setCategory(catResponse.data);
        // Fetch dynamic listing schema (for enriched-card grouping)
        try {
          const slug = catResponse.data?.slug || response.data.category_slug;
          if (slug) {
            const schemaRes = await axios.get(`${API}/form-schemas/by-category/${slug}?schema_type=listing`);
            if (schemaRes.data) setListingSchemaData(schemaRes.data);
          }
        } catch (_) { /* schema optional */ }
      }

      // Fetch similar listings using advanced search API
      try {
        const similarResponse = await axios.get(`${API}/search/similar/${listingId}?limit=6`);
        setRelatedListings(similarResponse.data.similar_listings || []);
      } catch (e) { console.error(e);
        // Fallback to category-based related listings
        const relatedResponse = await axios.get(`${API}/listings?category_id=${response.data.category_id}&limit=6`);
        setRelatedListings(relatedResponse.data.listings?.filter(l => l.listing_id !== listingId) || []);
      }

    } catch (error) {
      toast.error('Listing not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [listingId, navigate]);

  // Send message to listing owner
  const handleSendMessage = async () => {
    if (!currentUser) {
      toast.error('Log in to send a message');
      return;
    }
    if (!messageText.trim()) {
      toast.error('Please write a message');
      return;
    }
    if (listing.user_id === currentUser.user_id) {
      toast.error('You cannot send a message to your own listing');
      return;
    }

    setSendingMessage(true);
    try {
      // Iter405-msg-fix: atomic single-call create+send (initial_message).
      // Old 2-step flow could leave an empty conversation if step 2 failed,
      // and made the user see "sent" toast even though no message was
      // actually delivered. Backend POST /conversations handles initial_message
      // in the same transaction.
      const convRes = await axios.post(`${API}/conversations`, {
        recipient_id: listing.user_id,
        listing_id: listing.listing_id,
        initial_message: messageText.trim(),
      });

      toast.success('Your message has been sent');
      setMessageDialogOpen(false);
      setMessageText('');
      navigate(`/messages/${convRes.data.conversation_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Price alert functions
  const checkPriceAlert = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/price-alerts`);
      const alerts = response.data.alerts || [];
      const hasAlert = alerts.some(a => a.listing_id === listingId && a.is_active);
      setHasPriceAlert(hasAlert);
    } catch (error) {
      // Not logged in or error - ignore
    }
  }, [listingId]);

  useEffect(() => {
    fetchListing();
    fetchCurrentUser();
    checkPriceAlert();
    fetchReviews();
    checkJobApplication();
  }, [listingId, checkJobApplication, checkPriceAlert, fetchListing, fetchReviews]);

  const handleCreatePriceAlert = async () => {
    if (!currentUser) {
      toast.error('Please sign in to set a price alert');
      return;
    }
    if (!targetPrice || isNaN(parseFloat(targetPrice))) {
      toast.error('Enter a valid target price');
      return;
    }

    setSavingAlert(true);
    try {
      await axios.post(`${API}/price-alerts`, {
        listing_id: listingId,
        target_price: parseFloat(targetPrice),
        alert_type: alertType
      });
      toast.success('Price alert set!');
      setPriceAlertDialogOpen(false);
      setHasPriceAlert(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not set the alert');
    } finally {
      setSavingAlert(false);
    }
  };

  // iter397: İş listingslarında "fiyat" değil "ücret" kelimesi kullanılır.
  // salary_min/salary_max doluysa aralık, tek varsa "...dan itibaren" /
  // "en fazla ..." show, ikisi de yoksa "Fee not specified".
  const formatPrice = (price, priceType) => {
    const attrs = (listing && listing.attributes) || {};
    const sMin = Number(attrs.salary_min) || 0;
    const sMax = Number(attrs.salary_max) || 0;
    const isJob = (
      (category && category.slug === 'is-ilanlari') ||
      listing?.section === 'is-ilanlari' ||
      listing?.section_slug === 'is-ilanlari' ||
      !!(attrs.salary_type || attrs.job_type || attrs.sector || attrs.position)
    );
    if (isJob) {
      const fmt = (n) => `£${new Intl.NumberFormat('en-GB').format(Number(n))}`;
      if (sMin > 0 && sMax > 0) return `${fmt(sMin)} - ${fmt(sMax)}`;
      if (sMin > 0) return `${fmt(sMin)} and up`;
      if (sMax > 0) return `en fazla ${fmt(sMax)}`;
      if (price && Number(price) > 0) return `£${new Intl.NumberFormat('en-GB').format(price)}`;
      return 'Fee not specified';
    }
    if (!price) return 'Price not specified';
    const formatted = new Intl.NumberFormat('en-GB').format(price);
    const suffix = priceType === 'daily' ? '/day' : priceType === 'monthly' ? '/month' : priceType === 'hourly' ? '/hour' : '';
    return `£${formatted}${suffix}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async () => {
    // Use backend OG endpoint so Facebook/WhatsApp/etc crawlers get proper
    // listing-specific meta tags. Real users get redirected to /listing/{id}.
    const ogUrl = `${API}/og/listing/${listingId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: listing.title,
          text: listing.description?.slice(0, 100),
          url: ogUrl,
        });
        return;
      } catch (e) { console.error(e); /* user cancelled */ }
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
    
    // Fallback: copy OG URL to clipboard
    try {
      await navigator.clipboard.writeText(ogUrl);
      toast.success('Link copied');
    } catch (e) { console.error(e);
      toast.success(`Link: ${window.location.href}`, { duration: 5000 });
    }
  };

  if (loading) {
    return (
      <div className="container-app py-8">
        <Skeleton className="h-96 w-full rounded-2xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-8 w-1/4" />
            <Skeleton className="h-32 w-full" />
          </div>
          <div>
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return null;
  }

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const listingImage = listing?.images?.[0] ? (listing.images[0].startsWith('http') ? listing.images[0] : `${siteUrl}${listing.images[0]}`) : undefined;

  return (
    <div className="min-h-screen bg-slate-50 pb-36 md:pb-8">
      {listing && (
        <SEOHead
          title={generateListingTitle(listing)}
          description={generateListingDescription(listing)}
          image={listingImage}
          type="product"
          jsonLd={[
            pickListingSchema(listing, category, siteUrl),
            breadcrumbSchema(
              [
                { name: 'Home', path: '/' },
                category ? { name: category.name || category.slug, path: `/category/${category.slug}` } : null,
                { name: listing.title || 'Listing', path: `/listing/${listing.listing_id}` },
              ].filter(Boolean),
              siteUrl,
            ),
          ]}
        />
      )}
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container-app py-3 md:py-4">
          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm overflow-x-auto whitespace-nowrap">
            <Link to="/" className="text-muted-foreground hover:text-emerald-900 flex-shrink-0">Home</Link>
            <span className="text-muted-foreground flex-shrink-0">/</span>
            {category && (
              <>
                <Link to={
                  category.slug === 'emlak' ? '/property' :
                  category.slug === 'arac' ? '/vehicles' :
                  category.slug === 'hizmetler' ? '/services' :
                  category.slug === 'is-makinesi' ? '/heavy-machinery' :
                  category.slug === 'tarim' ? '/agriculture-services' :
                  category.slug === 'nakliye' ? '/shipping' :
                  category.slug === 'etkinlikler' ? '/events' :
                  category.slug === 'market' ? '/market' :
                  `/category/${category.slug}`
                } className="text-muted-foreground hover:text-emerald-900 flex-shrink-0">
                  {category.name}
                </Link>
                <span className="text-muted-foreground flex-shrink-0">/</span>
              </>
            )}
            <span className="text-emerald-900 font-medium truncate max-w-[200px]">{listing.title}</span>
          </div>
        </div>
      </div>

      <div className="container-app py-8">
        {/* Back button - mobile */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 lg:hidden"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Carousel */}
            <Card className="overflow-hidden">
              {listing.images?.length > 0 ? (
                <Carousel className="w-full">
                  <CarouselContent>
                    {listing.images.map((image, index) => (
                      <CarouselItem key={`${image}-${index}`}>
                        <div 
                          className="aspect-[16/9] relative cursor-pointer group"
                          onClick={() => {
                            setLightboxIndex(index);
                            setLightboxOpen(true);
                          }}
                        >
                          <img loading="lazy" decoding="async"
                            src={image}
                            alt={`${listing.title} - ${index + 1}`}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                              <Eye className="w-6 h-6 text-slate-700" />
                            </div>
                          </div>
                          {/* Owner Logo Watermark */}
                          <div className="absolute bottom-3 right-3 z-[5] opacity-35 mix-blend-luminosity pointer-events-none" data-testid="detail-owner-logo-watermark">
                            <img loading="lazy" decoding="async" src={watermarkSrc()} alt="" className="w-14 h-14 rounded object-contain drop-shadow-[0_0_3px_rgba(255,255,255,0.9)]" onError={(e) => { e.target.src = watermarkFallback; }} />
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {listing.images.length > 1 && (
                    <>
                      <CarouselPrevious className="left-4" />
                      <CarouselNext className="right-4" />
                    </>
                  )}
                </Carousel>
              ) : (
                <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center gap-2 rounded-lg">
                  <Tag className="w-10 h-10 text-slate-300" />
                  <span className="text-xs text-slate-400">No image added</span>
                </div>
              )}
            </Card>

            {/* Title & Price */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {listing.is_featured && (
                      <Badge className="bg-[#f97316]" data-testid="featured-badge">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    {category && (
                      <Badge variant="outline">{category.name}</Badge>
                    )}
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-emerald-900" data-testid="listing-title">
                    {listing.title}
                  </h1>
                  <p className="text-xs text-slate-400 mt-1 font-mono" data-testid="listing-id">
                    Listing No: {listing.listing_id?.replace('list_', '').substring(0, 8).toUpperCase()}
                  </p>
                </div>
                <div className="flex gap-2">
                  {/* Edit button - only visible to owner */}
                  {currentUser && listing.user_id === currentUser.user_id && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => navigate(
                        category?.slug === 'etkinlikler'
                          ? `/event/edit/${listingId}`
                          : `/edit-listing/${listingId}`
                      )}
                      className="text-blue-600 hover:bg-blue-50"
                      data-testid="edit-listing-btn"
                    >
                      <Edit2 className="w-5 h-5" />
                    </Button>
                  )}
                  <FavoriteButton listingId={listingId} size="lg" />
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => {
                      if (hasPriceAlert) {
                        navigate('/price-alerts');
                      } else {
                        setTargetPrice(Math.floor((listing.price || 0) * 0.9).toString());
                        setPriceAlertDialogOpen(true);
                      }
                    }}
                    className={hasPriceAlert ? 'text-amber-500 border-amber-500 hover:bg-amber-50' : ''}
                    data-testid="price-alert-btn"
                    title="Set Price Alert"
                  >
                    <Bell className={`w-5 h-5 ${hasPriceAlert ? 'fill-current' : ''}`} />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleShare} data-testid="share-btn">
                    <Share2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <p className={listing.price ? 'text-3xl md:text-4xl font-bold text-[#f97316] mb-4' : 'text-sm text-slate-400 font-medium mb-4'} data-testid="listing-price">
                {formatPrice(listing.price, listing.price_type)}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1 min-w-0 max-w-full">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="break-words min-w-0">
                    {listing.location?.region_name || listing.location?.province_name || listing.location?.region || ''}
                    {listing.location?.district && `, ${listing.location.district}`}
                  </span>
                </span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  <Eye className="w-4 h-4" />
                  {listing.views} views
                </span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  <Calendar className="w-4 h-4" />
                  {formatDate(listing.created_at)}
                </span>
              </div>
            </div>

            {/* Seller Info Strip - compact, left column */}
            {owner && (
              <Link to={`/service-provider/${listing.user_id}`} className="block" data-testid="seller-info-link">
                <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer" data-testid="seller-info-strip">
                  <div className="w-9 h-9 rounded-full bg-emerald-900/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {owner.picture ? (
                      <img loading="lazy" decoding="async" src={owner.picture} alt="" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <span className="text-sm font-bold text-emerald-900">{(owner.name || 'U')[0].toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800 truncate hover:text-emerald-900 transition-colors">{owner.name || 'Listing Owner'}</span>
                      {ownerVerification?.is_verified && (
                        <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" title="Verified Account" />
                      )}
                      {ownerVerification?.badges?.some(b => b.id === 'phone_verified') && (
                        <span className="text-[10px] text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          <Phone className="w-2.5 h-2.5" />Phone
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">Platform Member</span>
                  </div>
                  <span className="text-xs text-emerald-900 flex-shrink-0">
                    View Profile →
                  </span>
                </div>
              </Link>
            )}

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 whitespace-pre-wrap" data-testid="listing-description">
                  {listing.description}
                </p>
              </CardContent>
            </Card>

            {/* Enriched Schema Card — grouped Rightmove/AutoTrader-style details */}
            {listingSchemaData && (
              <EnrichedSchemaCard
                schema={listingSchemaData}
                formData={{...(listing.filters || {}), ...(listing.attributes || {})}}
                title="Listing Details"
                data-testid="listing-enriched-details"
              />
            )}

            {/* Attributes */}
            {/* Attributes (legacy flat list — hidden when EnrichedSchemaCard is showing) */}
            {!listingSchemaData && ((listing.attributes && Object.keys(listing.attributes).length > 0) || (listing.filters && Object.keys(listing.filters).length > 0)) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Features</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {Object.entries({...listing.filters, ...listing.attributes})
                      .filter(([key, value]) => shouldShowAttribute(key, value, listing.attributes?.listing_type))
                      .map(([key, value]) => {
                        const displayValue = formatAttributeValue(key, value);
                        if (displayValue === null) return null;
                        const label = ATTRIBUTE_LABELS[key] || getFieldLabel(key);
                        
                        return (
                          <div key={key} className="flex items-start gap-2 min-w-0">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm min-w-0 break-words leading-snug">
                              <span className="font-medium">{label}:</span> {displayValue}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hasar Registrationları Details */}
            {listing.attributes?.damage_records && Array.isArray(listing.attributes.damage_records) && listing.attributes.damage_records.length > 0 && (
              <Card data-testid="damage-records-card">
                <CardHeader>
                  <CardTitle className="text-lg text-red-600">Damage Records ({listing.attributes.damage_records.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {listing.attributes.damage_records.map((record, idx) => (
                      <div key={`${record}-${idx}`} className="border rounded-lg p-3 bg-red-50/50">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {record.amount && (
                            <div>
                              <span className="text-xs text-muted-foreground block">Damage Amount</span>
                              <span className="font-semibold text-red-600">£{new Intl.NumberFormat('en-GB').format(typeof record.amount === 'string' ? parseInt(record.amount.replace(/\./g, '')) : record.amount)}</span>
                            </div>
                          )}
                          {record.date && (
                            <div>
                              <span className="text-xs text-muted-foreground block">Date</span>
                              <span className="font-medium">{new Date(record.date).toLocaleDateString('en-GB')}</span>
                            </div>
                          )}
                          {record.description && (
                            <div>
                              <span className="text-xs text-muted-foreground block">Description</span>
                              <span className="text-sm">{record.description}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ratingler */}
            {reviews.length > 0 && (
              <Card data-testid="reviews-section">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    Ratingler ({reviews.length})
                    {listing.avg_rating > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 ml-2">
                        {listing.avg_rating} / 5
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {reviews.map((review, idx) => (
                      <div key={review.review_id || idx} className="border-b last:border-b-0 pb-4 last:pb-0">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={review.reviewer_avatar} />
                            <AvatarFallback className="bg-slate-100 text-slate-600 text-sm">
                              {(review.reviewer_name || 'K')[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm text-slate-800">{review.reviewer_name || 'User'}</span>
                              <span className="text-xs text-muted-foreground">
                                {review.created_at ? new Date(review.created_at).toLocaleDateString('en-GB') : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 mb-2">
                              {[...Array(5)].map((_, i) => (
                                <Star key={`star-${i}`} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                              ))}
                            </div>
                            {review.comment && (
                              <p className="text-sm text-slate-600">{review.comment}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Location Map - Emlak listingsları for */}
            {listing.location?.lat && listing.location?.lng && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Location</CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowMap(!showMap)}
                    data-testid="toggle-map-btn"
                  >
                    <Map className="w-4 h-4 mr-2" />
                    {showMap ? 'Hide Map' : 'Show on Map'}
                  </Button>
                </CardHeader>
                {showMap && (
                  <CardContent>
                    <div className="h-[300px] rounded-lg overflow-hidden" data-testid="listing-map">
                      <MapContainer
                        center={[listing.location.lat, listing.location.lng]}
                        zoom={15}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={false}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[listing.location.lat, listing.location.lng]}>
                          <Popup>
                            <div className="text-center">
                              <p className="font-medium">{listing.title}</p>
                              {listing.location.address && (
                                <p className="text-sm text-muted-foreground">{listing.location.address}</p>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                    {listing.location.address && (
                      <p className="text-sm text-muted-foreground mt-2 flex items-start gap-1 min-w-0">
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="break-words min-w-0">{listing.location.address}</span>
                      </p>
                    )}
                  </CardContent>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar - Desktop only; mobile uses bottom action bar + seller strip */}
          <div className="hidden lg:block space-y-6">
            {/* Professional Provider Card - for service providers */}
            {(category?.system_type === 'service_marketplace' || listing.filters?.service_category) ? (
              <Card className="overflow-hidden" data-testid="provider-profile-card">
                {/* Provider Header */}
                <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-5 text-white text-center">
                  <Avatar className="h-16 w-16 mx-auto border-3 border-white/30 mb-3">
                    <AvatarImage src={owner?.picture || owner?.profile_photo_url || listing.seller?.avatar} />
                    <AvatarFallback className="bg-white/20 text-white text-xl">
                      {(owner?.name || listing.seller?.name || 'H')[0]}
                    </AvatarFallback>
                  </Avatar>
                  <h3 className="font-bold text-lg">{listing.title}</h3>
                  <p className="text-white/80 text-sm">{owner?.name || listing.seller?.name || 'Service Provider'}</p>

                  {/* Star Rating */}
                  {listing.avg_rating > 0 && (
                    <div className="flex items-center justify-center gap-1.5 mt-2">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star key={`star-${i}`} className={`w-4 h-4 ${i < Math.round(listing.avg_rating) ? 'text-amber-300 fill-amber-300' : 'text-white/30'}`} />
                        ))}
                      </div>
                      <span className="font-bold text-amber-200">{listing.avg_rating}</span>
                      <span className="text-white/60 text-sm">({listing.review_count} reviews)</span>
                    </div>
                  )}
                </div>

                <CardContent className="p-4 space-y-3">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {listing.filters?.experience_years && (
                      <div className="bg-slate-50 rounded-lg p-2">
                        <Clock className="w-4 h-4 mx-auto mb-1 text-teal-600" />
                        <p className="text-sm font-bold text-slate-800">{listing.filters.experience_years}</p>
                        <p className="text-[10px] text-muted-foreground">Years Experience</p>
                      </div>
                    )}
                    <div className="bg-slate-50 rounded-lg p-2">
                      <Eye className="w-4 h-4 mx-auto mb-1 text-teal-600" />
                      <p className="text-sm font-bold text-slate-800">{listing.views || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Views</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <Star className="w-4 h-4 mx-auto mb-1 text-amber-500" />
                      <p className="text-sm font-bold text-slate-800">{listing.review_count || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Review</p>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-1.5">
                    {listing.filters?.certified && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                        <Award className="w-3 h-3 mr-1" />
                        Certified
                      </Badge>
                    )}
                    {listing.filters?.insured && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        Insured
                      </Badge>
                    )}
                    {listing.filters?.guarantee && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Work Guarantee
                      </Badge>
                    )}
                    {ownerVerification?.is_verified && (
                      <Badge className="bg-sky-100 text-sky-700 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-2 text-sm text-muted-foreground min-w-0">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="break-words min-w-0">{listing.location?.region_name ? `${listing.location.region_name}, ${listing.location.province_name}` : listing.location?.province_name || listing.location?.region || ''}</span>
                  </div>

                  {/* Price - only for classified listings, not service providers */}
                  {listing.price > 0 && category?.system_type !== 'service_marketplace' && !listing.filters?.service_category && (
                    <div className="bg-teal-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-teal-600 mb-1">Price</p>
                      <p className={listing.price ? 'text-xl font-bold text-teal-700' : 'text-xs text-slate-400 font-medium'} data-testid="listing-price">
                        {formatPrice(listing.price, listing.price_type)}
                      </p>
                    </div>
                  )}

                  {/* Contact Actions */}
                  {currentUser && currentUser.user_id !== listing.user_id && (
                    <Button 
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white" 
                      onClick={() => setMessageDialogOpen(true)}
                      data-testid="message-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  )}
                  {!currentUser && (
                    <Button 
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white" 
                      onClick={() => toast.error('Log in to send a message')}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  )}

                  {listing.contact_phone && !listing.owner_hide_phone && (
                    <Button 
                      className="w-full btn-secondary" 
                      onClick={() => window.location.href = `tel:${listing.contact_phone}`}
                      data-testid="call-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      {listing.contact_phone}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
            /* Standard Contact Card - for regular listings */
            <Card className="">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {currentUser && currentUser.user_id !== listing.user_id && (
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-[#2980b9] text-white" 
                    onClick={() => setMessageDialogOpen(true)}
                    data-testid="message-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                )}
                {!currentUser && (
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-[#2980b9] text-white" 
                    onClick={() => toast.error('Log in to send a message')}
                    data-testid="message-btn-login"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                )}

                {(owner?.phone || listing.contact_phone) && !listing.owner_hide_phone && (
                  <Button 
                    className="w-full btn-secondary" 
                    onClick={() => window.location.href = `tel:${owner?.phone || listing.contact_phone}`}
                    data-testid="call-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    {owner?.phone || listing.contact_phone}
                  </Button>
                )}

                {(owner?.email || listing.contact_email) && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.location.href = `mailto:${owner?.email || listing.contact_email}`}
                    data-testid="email-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                )}

                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground text-center">
                    Please mention you contacted them via the platform.
                  </p>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Job Application Button - İş Listingları for */}
            {category?.slug === 'is-ilanlari' && (
              <Card className="border-2 border-emerald-200" data-testid="job-apply-card">
                <CardContent className="p-5 text-center">
                  <Briefcase className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-lg text-slate-800 mb-2">Apply to This Listing</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {listing.attributes?.job_type && `${listing.attributes.job_type} pozisyon`}
                    {listing.attributes?.salary_range && ` | Salary: £${listing.attributes.salary_range}`}
                  </p>
                  {hasApplied ? (
                    <Badge className="bg-emerald-100 text-emerald-700 px-4 py-2 text-sm">
                      <CheckCircle className="w-4 h-4 mr-1.5" />
                      Application Received
                    </Badge>
                  ) : (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={openJobAppDialog}
                      data-testid="job-apply-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Apply Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Safety Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-[#f97316]">Safety Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Don't pay before seeing the item
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Don't share personal information
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Meet in safe public places
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Related Listings */}
        {relatedListings.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-bold text-emerald-900 mb-6">
              Similar Listings
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedListings.map((item) => (
                <Link key={item.listing_id} to={`/listing/${item.listing_id}`} data-testid={`related-${item.listing_id}`}>
                  <Card className="card-listing h-full">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      {item.images?.[0] ? (
                        <img loading="lazy" decoding="async"
                          src={item.images[0]}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                          <Tag className="w-8 h-8 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium text-slate-800 line-clamp-1 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-lg font-bold text-[#f97316]">
                        {formatPrice(item.price, item.price_type)}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Fixed Action Bar */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white border-t p-4 md:hidden">
        <div className="flex gap-3">
          {currentUser && currentUser.user_id !== listing.user_id && (
            <Button 
              className="flex-1 bg-emerald-600 hover:bg-[#2980b9] text-white"
              onClick={() => setMessageDialogOpen(true)}
              data-testid="mobile-message-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Message
            </Button>
          )}
          {!currentUser && (
            <Button 
              className="flex-1 bg-emerald-600 hover:bg-[#2980b9] text-white"
              onClick={() => toast.error('Log in to send a message')}
              data-testid="mobile-message-btn-login"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Message
            </Button>
          )}
          {listing.contact_phone && !listing.owner_hide_phone && (
            <Button 
              className="flex-1 btn-secondary"
              onClick={() => window.location.href = `tel:${listing.contact_phone}`}
              data-testid="mobile-call-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call
            </Button>
          )}
          {/* İş Listingı Başvuru Butonu - Mobil */}
          {category?.slug === 'is-ilanlari' && currentUser && currentUser.user_id !== listing.user_id && !hasApplied && (
            <Button 
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={openJobAppDialog}
              data-testid="mobile-job-apply-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Apply
            </Button>
          )}
          {category?.slug === 'is-ilanlari' && hasApplied && (
            <Badge className="flex-1 bg-emerald-100 text-emerald-700 px-4 py-2 text-sm justify-center">
              <CheckCircle className="w-4 h-4 mr-1.5" />
              Applied
            </Badge>
          )}
          {currentUser && listing.user_id === currentUser.user_id && (
            <Button 
              variant="outline" 
              className="flex-1 text-blue-600 hover:bg-blue-50"
              onClick={() => navigate(
                category?.slug === 'etkinlikler'
                  ? `/event/edit/${listingId}`
                  : `/edit-listing/${listingId}`
              )}
              data-testid="mobile-edit-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          <Button variant="outline" size="icon" className="h-10 w-10 flex-shrink-0" onClick={handleShare} data-testid="mobile-share-btn">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <MessageDialog
        open={messageDialogOpen} onOpenChange={setMessageDialogOpen}
        listing={listing} owner={owner}
        messageText={messageText} setMessageText={setMessageText}
        sendingMessage={sendingMessage} onSend={handleSendMessage}
      />

      <PriceAlertDialog
        open={priceAlertDialogOpen} onOpenChange={setPriceAlertDialogOpen}
        listing={listing}
        targetPriceDisplay={targetPriceDisplay} setTargetPrice={setTargetPrice} setTargetPriceDisplay={setTargetPriceDisplay}
        alertType={alertType} setAlertType={setAlertType}
        savingAlert={savingAlert} onSave={handleCreatePriceAlert}
      />

      <ImageLightbox
        open={lightboxOpen} onOpenChange={setLightboxOpen}
        images={listing?.images} index={lightboxIndex} setIndex={setLightboxIndex}
        title={listing?.title}
      />

      <JobApplicationDialog
        open={jobAppDialogOpen} onOpenChange={setJobAppDialogOpen}
        listing={listing}
        jobAppForm={jobAppForm} setJobAppForm={setJobAppForm}
        submittingApp={submittingApp} onSubmit={handleJobAppSubmit}
        uploadingCV={uploadingCV} onCVUpload={handleCVUpload}
      />
    </div>
  );
};

export default ListingDetailPage;
